import React from "react";
import axios from "axios";

import "./Merchant.css";

const emptySummary = {
  total: 0,
  open_merchants: 0,
  closed_merchants: 0,
  average_admin_fee: 0,
};

const getInitials = name => (name || "Merchant")
  .split(" ")
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0))
  .join("")
  .toUpperCase();

const nullableValue = value => value === null || value === undefined ? "" : value;

class Merchant extends React.Component {
  state = {
    merchants: [],
    summary: emptySummary,
    search: "",
    status: "",
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
    loading: true,
    error: "",
    selectedMerchant: null,
    form: null,
    saving: false,
    formError: "",
    successMessage: "",
  };

  componentDidMount() {
    this.fetchMerchants();
  }

  fetchMerchants = async (page = 1, options = {}) => {
    const search = options.search !== undefined ? options.search : this.state.search;
    const status = options.status !== undefined ? options.status : this.state.status;
    const perPage = options.perPage || this.state.perPage;

    this.setState({ loading: true, error: "", successMessage: "" });

    try {
      const response = await axios.get(global.variable.LINK_API + "merchant", {
        ...global.variable.HEADERS_BEARER,
        cancelKey: "merchant-list",
        params: { page, per_page: perPage, search: search.trim(), status },
      });
      this.setState({
        merchants: response.data.data || [],
        summary: response.data.summary || emptySummary,
        currentPage: response.data.current_page || 1,
        lastPage: response.data.last_page || 1,
        total: response.data.total || 0,
        perPage,
      });
    } catch (error) {
      this.setState({
        merchants: [],
        error: error?.response?.data?.message || "Data merchant belum dapat dimuat.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  handleFilter = event => {
    this.setState({ [event.target.name]: event.target.value });
  };

  applyFilter = event => {
    event.preventDefault();
    this.fetchMerchants(1);
  };

  resetFilter = () => {
    this.setState({ search: "", status: "" }, () => {
      this.fetchMerchants(1, { search: "", status: "" });
    });
  };

  openEditor = merchant => {
    this.setState({
      selectedMerchant: merchant,
      form: {
        name: nullableValue(merchant.name),
        slogan: nullableValue(merchant.slogan),
        title: nullableValue(merchant.title),
        description: nullableValue(merchant.description),
        admin_fee_by: String(merchant.admin_fee_by),
        admin_fee: String(merchant.admin_fee),
        is_open: String(merchant.is_open),
      },
      formError: "",
      successMessage: "",
    });
  };

  closeEditor = () => {
    if (this.state.saving) return;
    this.setState({ selectedMerchant: null, form: null, formError: "" });
  };

  updateForm = event => {
    const { name, value } = event.target;
    this.setState(currentState => ({
      form: { ...currentState.form, [name]: value },
      formError: "",
    }));
  };

  getValidationMessage = error => {
    const errors = error?.response?.data?.errors;
    if (typeof errors === "string") return errors;
    if (errors && typeof errors === "object") {
      const firstError = Object.values(errors)[0];
      return Array.isArray(firstError) ? firstError[0] : String(firstError);
    }
    return error?.response?.data?.message || "Merchant gagal diperbarui.";
  };

  saveMerchant = async event => {
    event.preventDefault();
    const { selectedMerchant, form } = this.state;

    if (!form.name.trim()) {
      this.setState({ formError: "Nama merchant wajib diisi." });
      return;
    }

    const adminFee = Number(form.admin_fee);
    if (!Number.isInteger(adminFee) || adminFee < 0 || adminFee > 100) {
      this.setState({ formError: "Admin fee harus berupa angka bulat antara 0 sampai 100%." });
      return;
    }

    this.setState({ saving: true, formError: "" });

    try {
      const response = await axios.put(
        global.variable.LINK_API + `merchant/${selectedMerchant.id}`,
        {
          ...form,
          admin_fee: adminFee,
          admin_fee_by: Number(form.admin_fee_by),
          is_open: Number(form.is_open),
        },
        global.variable.HEADERS_BEARER
      );
      const successMessage = response.data.message || "Merchant berhasil diperbarui.";

      this.setState({
        selectedMerchant: null,
        form: null,
      });
      await this.fetchMerchants(this.state.currentPage);
      this.setState({ successMessage });
    } catch (error) {
      this.setState({ formError: this.getValidationMessage(error) });
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const {
      merchants, summary, search, status, currentPage, lastPage, perPage, total,
      loading, error, selectedMerchant, form, saving, formError, successMessage,
    } = this.state;
    const firstRow = total ? (currentPage - 1) * perPage + 1 : 0;
    const lastRow = Math.min(currentPage * perPage, total);

    return (
      <section className="merchant-page">
        {successMessage && <div className="merchant-alert merchant-alert--success"><i className="fas fa-check-circle" />{successMessage}</div>}

        <div className="merchant-summary">
          <article><span className="merchant-summary__icon merchant-summary__icon--purple"><i className="fas fa-store" /></span><div><small>Total merchant</small><strong>{Number(summary.total).toLocaleString("id-ID")}</strong></div></article>
          <article><span className="merchant-summary__icon merchant-summary__icon--green"><i className="fas fa-door-open" /></span><div><small>Merchant buka</small><strong>{Number(summary.open_merchants).toLocaleString("id-ID")}</strong></div></article>
          <article><span className="merchant-summary__icon merchant-summary__icon--red"><i className="fas fa-store-slash" /></span><div><small>Merchant tutup</small><strong>{Number(summary.closed_merchants).toLocaleString("id-ID")}</strong></div></article>
          <article><span className="merchant-summary__icon merchant-summary__icon--blue"><i className="fas fa-percentage" /></span><div><small>Rata-rata admin fee</small><strong>{Number(summary.average_admin_fee).toLocaleString("id-ID")}%</strong></div></article>
        </div>

        <div className="merchant-card">
          <form className="merchant-filter" onSubmit={this.applyFilter}>
            <div><span><i className="fas fa-search" /></span><input type="search" name="search" value={search} onChange={this.handleFilter} placeholder="Cari nama, subdomain, atau email" /></div>
            <select name="status" value={status} onChange={this.handleFilter}><option value="">Semua status</option><option value="1">Buka</option><option value="0">Tutup</option></select>
            <button type="button" className="merchant-filter__reset" onClick={this.resetFilter}>Reset</button>
            <button type="submit" className="merchant-filter__submit">Terapkan filter</button>
          </form>

          <div className="merchant-card__heading">
            <div><h2>Data merchant</h2><p>{total.toLocaleString("id-ID")} merchant ditemukan</p></div>
            <div className="merchant-card__actions"><button type="button" className="merchant-card__refresh" onClick={() => this.fetchMerchants(currentPage)} disabled={loading}><i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} /> Refresh</button><label>Tampilkan <select value={perPage} onChange={event => this.fetchMerchants(1, { perPage: Number(event.target.value) })}><option value={10}>10</option><option value={25}>25</option><option value={50}>50</option></select> baris</label></div>
          </div>

          {error ? (
            <div className="merchant-state merchant-state--error"><span><i className="fas fa-exclamation-triangle" /></span><h3>Gagal memuat merchant</h3><p>{error}</p><button type="button" onClick={() => this.fetchMerchants(currentPage)}>Coba lagi</button></div>
          ) : loading ? (
            <div className="merchant-state"><span className="merchant-state__loader" /><h3>Memuat data merchant</h3><p>Mohon tunggu sebentar.</p></div>
          ) : merchants.length === 0 ? (
            <div className="merchant-state"><span><i className="fas fa-store" /></span><h3>Merchant tidak ditemukan</h3><p>Ubah kata kunci atau filter yang digunakan.</p></div>
          ) : (
            <div className="merchant-table-wrap">
              <table className="merchant-table">
                <thead><tr><th>Merchant</th><th>Domain</th><th>Status</th><th>Admin fee</th><th>Ditanggung oleh</th><th /></tr></thead>
                <tbody>{merchants.map(merchant => (
                  <tr key={merchant.id}>
                    <td data-label="Merchant"><div className="merchant-identity"><span>{getInitials(merchant.name)}</span><div><strong>{merchant.name || "Tanpa nama"}</strong><small>{merchant.email || "Email tidak tersedia"}</small></div></div></td>
                    <td data-label="Domain"><strong className="merchant-domain">{merchant.domain || merchant.subdomain || "-"}</strong><small>{merchant.subdomain ? `${merchant.subdomain}.1toko.com` : ""}</small></td>
                    <td data-label="Status"><span className={`merchant-status merchant-status--${Number(merchant.is_open) === 1 ? "open" : "closed"}`}><i />{Number(merchant.is_open) === 1 ? "Buka" : "Tutup"}</span></td>
                    <td data-label="Admin fee"><strong className="merchant-fee">{Number(merchant.admin_fee).toLocaleString("id-ID")}%</strong></td>
                    <td data-label="Ditanggung"><span className="merchant-fee-by">{Number(merchant.admin_fee_by) === 1 ? "Merchant" : "Pelanggan"}</span></td>
                    <td data-label="Aksi"><button type="button" className="merchant-edit" onClick={() => this.openEditor(merchant)}><i className="fas fa-pen" /> Edit</button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}

          {!loading && !error && total > 0 && <footer className="merchant-pagination"><p>Menampilkan <strong>{firstRow}-{lastRow}</strong> dari <strong>{total.toLocaleString("id-ID")}</strong></p><div><button type="button" disabled={currentPage <= 1} onClick={() => this.fetchMerchants(currentPage - 1)}><i className="fas fa-chevron-left" /></button><span>Halaman {currentPage} dari {lastPage}</span><button type="button" disabled={currentPage >= lastPage} onClick={() => this.fetchMerchants(currentPage + 1)}><i className="fas fa-chevron-right" /></button></div></footer>}
        </div>

        {selectedMerchant && form && (
          <div className="merchant-modal" role="dialog" aria-modal="true" aria-labelledby="merchant-editor-title">
            <button type="button" className="merchant-modal__backdrop" onClick={this.closeEditor} aria-label="Tutup form" />
            <form className="merchant-editor" onSubmit={this.saveMerchant}>
              <header><div><span className="merchant-editor__avatar">{getInitials(selectedMerchant.name)}</span><div><small>EDIT MERCHANT</small><h2 id="merchant-editor-title">{selectedMerchant.name}</h2><p>{selectedMerchant.email}</p></div></div><button type="button" onClick={this.closeEditor} disabled={saving}><i className="fas fa-times" /></button></header>
              <div className="merchant-editor__body">
                {formError && <div className="merchant-alert merchant-alert--error"><i className="fas fa-exclamation-circle" />{formError}</div>}
                <section><div className="merchant-editor__section-title"><span><i className="fas fa-store" /></span><div><h3>Informasi merchant</h3><p>Informasi publik yang ditampilkan untuk merchant.</p></div></div>
                  <label><span>Nama merchant *</span><input name="name" value={form.name} onChange={this.updateForm} maxLength={255} /></label>
                  <div className="merchant-editor__grid"><label><span>Slogan</span><input name="slogan" value={form.slogan} onChange={this.updateForm} maxLength={255} /></label><label><span>Judul toko</span><input name="title" value={form.title} onChange={this.updateForm} maxLength={255} /></label></div>
                  <label><span>Deskripsi</span><textarea name="description" value={form.description} onChange={this.updateForm} rows={4} /></label>
                </section>
                <section className="merchant-editor__fee-section"><div className="merchant-editor__section-title"><span><i className="fas fa-percentage" /></span><div><h3>Konfigurasi admin fee</h3><p>Persentase biaya yang diterapkan pada transaksi merchant.</p></div></div>
                  <div className="merchant-editor__grid"><label><span>Admin fee (%) *</span><div className="merchant-editor__fee-input"><input type="number" name="admin_fee" value={form.admin_fee} onChange={this.updateForm} min="0" max="100" step="1" /><i>%</i></div></label><label><span>Ditanggung oleh *</span><select name="admin_fee_by" value={form.admin_fee_by} onChange={this.updateForm}><option value="1">Merchant</option><option value="0">Pelanggan</option></select></label></div>
                  <label><span>Status operasional *</span><select name="is_open" value={form.is_open} onChange={this.updateForm}><option value="1">Buka</option><option value="0">Tutup</option></select></label>
                  <div className="merchant-editor__warning"><i className="fas fa-info-circle" /><span>Perubahan admin fee berlaku untuk transaksi berikutnya dan tidak mengubah histori transaksi lama.</span></div>
                </section>
              </div>
              <footer><button type="button" onClick={this.closeEditor} disabled={saving}>Batal</button><button type="submit" disabled={saving}>{saving ? <><span className="merchant-button-spinner" /> Menyimpan...</> : <><i className="fas fa-save" /> Simpan perubahan</>}</button></footer>
            </form>
          </div>
        )}
      </section>
    );
  }
}

export default Merchant;
